import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  Tooltip,
} from 'recharts';
import { getWine } from '../lib/api';
import { Button } from '../components/ui/button';
import { WineScore } from '../components/StarRating';
import { ArrowLeft, Grape, Calendar, UtensilsCrossed, X, Wine, GitCompare } from 'lucide-react';

// Palette for up to 4 wines — gold, burgundy, teal, rose
const PALETTE = ['#d4af37', '#9f1239', '#14b8a6', '#f472b6'];

// Convert text or arrays to 0-100 "complexity" scores for radar axes
const countTerms = (text) => {
  if (!text) return 0;
  return text.split(/[,;.\n]/).map(s => s.trim()).filter(Boolean).length;
};

const buildMetrics = (wine) => ({
  Rating: Math.min(100, wine.average_rating || 0),
  Popularity: Math.min(100, (wine.rating_count || 0) * 10),
  Complexity: Math.min(100, (wine.tasting_notes?.length || 0) / 3),
  Aroma: Math.min(100, countTerms(wine.aroma) * 18),
  Flavor: Math.min(100, countTerms(wine.flavor) * 18),
  Versatility: Math.min(100, (wine.food_pairings?.length || 0) * 15),
});

const METRIC_KEYS = ['Rating', 'Popularity', 'Complexity', 'Aroma', 'Flavor', 'Versatility'];

const WineCompare = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const ids = useMemo(() => {
    const raw = searchParams.get('ids') || '';
    return raw.split(',').map(s => s.trim()).filter(Boolean).slice(0, 4);
  }, [searchParams]);

  const [wines, setWines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ids.length === 0) {
      setWines([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all(ids.map((id) => getWine(id).catch(() => null)))
      .then((results) => setWines(results.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [ids]);

  const removeWine = (id) => {
    const remaining = ids.filter((x) => x !== id);
    if (remaining.length === 0) {
      setSearchParams({});
    } else {
      setSearchParams({ ids: remaining.join(',') });
    }
  };

  // Radar data — one row per metric, one column per wine
  const radarData = useMemo(() => {
    const metricsByWine = wines.map(buildMetrics);
    return METRIC_KEYS.map((key) => {
      const row = { metric: key };
      wines.forEach((w, i) => {
        row[w.name] = metricsByWine[i][key];
      });
      return row;
    });
  }, [wines]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-8 pb-20" data-testid="wine-compare-page">
      <div className="container mx-auto px-4 md:px-8">
        <button
          onClick={() => navigate('/wines')}
          className="flex items-center gap-2 text-stone-500 hover:text-stone-300 mb-8 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Wines
        </button>

        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2">Side-by-Side</p>
          <h1 className="text-4xl md:text-5xl font-serif text-stone-50 mb-2 flex items-center gap-3">
            <GitCompare className="w-8 h-8 text-[#d4af37]" />
            Wine Comparison
          </h1>
          <p className="text-stone-500 text-sm">
            {wines.length} wine{wines.length === 1 ? '' : 's'} selected
          </p>
        </div>

        {wines.length < 2 ? (
          <div className="text-center py-20" data-testid="compare-empty-state">
            <Wine className="w-16 h-16 text-stone-700 mx-auto mb-6" />
            <h3 className="text-2xl font-serif text-stone-400 mb-2">
              {wines.length === 0 ? 'No Wines Selected' : 'Add at Least One More Wine'}
            </h3>
            <p className="text-stone-600 mb-6">
              Go to the Wines page, check the "Compare" box on 2–4 wines, then click Compare.
            </p>
            <Button
              onClick={() => navigate('/wines')}
              className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs"
            >
              Browse Wines
            </Button>
          </div>
        ) : (
          <>
            {/* Radar Chart */}
            <div className="mb-12 p-6 bg-stone-900/30 border border-white/5" data-testid="compare-radar">
              <h2 className="text-xs uppercase tracking-widest text-[#d4af37] mb-6">
                Tasting Profile Radar
              </h2>
              <div className="h-[420px] w-full min-w-[320px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={420}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="rgba(212,175,55,0.15)" />
                    <PolarAngleAxis
                      dataKey="metric"
                      tick={{ fill: '#a8a29e', fontSize: 12, letterSpacing: '0.1em' }}
                    />
                    <PolarRadiusAxis
                      angle={30}
                      domain={[0, 100]}
                      tick={{ fill: '#57534e', fontSize: 10 }}
                      stroke="rgba(120,113,108,0.2)"
                    />
                    {wines.map((w, i) => (
                      <Radar
                        key={w.id}
                        name={w.name}
                        dataKey={w.name}
                        stroke={PALETTE[i]}
                        fill={PALETTE[i]}
                        fillOpacity={0.2}
                        strokeWidth={2}
                      />
                    ))}
                    <Legend
                      wrapperStyle={{ paddingTop: 16 }}
                      iconType="circle"
                      formatter={(value) => (
                        <span className="text-stone-300 text-xs">{value}</span>
                      )}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#1c1917',
                        border: '1px solid rgba(212,175,55,0.3)',
                        borderRadius: 0,
                        color: '#e7e5e4',
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-stone-600 mt-4 text-center">
                Each axis is scored 0–100 based on the wine's data. Larger shapes indicate richer profiles.
              </p>
            </div>

            {/* Comparison Table */}
            <div className="overflow-x-auto border border-white/5" data-testid="compare-table">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="bg-stone-900/60 border-b border-white/10">
                    <th className="text-left px-4 py-4 text-xs uppercase tracking-widest text-stone-500 w-40">
                      Attribute
                    </th>
                    {wines.map((w, i) => (
                      <th
                        key={w.id}
                        className="text-left px-4 py-4 align-top"
                        style={{ minWidth: 220 }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div
                              className="text-[10px] uppercase tracking-widest mb-1"
                              style={{ color: PALETTE[i] }}
                            >
                              <Grape className="w-3 h-3 inline mr-1" />
                              {w.grape_variety}
                            </div>
                            <Link
                              to={`/wines/${w.id}`}
                              className="text-base font-serif text-stone-50 hover:text-[#d4af37] transition-colors leading-tight"
                              data-testid={`compare-wine-link-${w.id}`}
                            >
                              {w.name}
                            </Link>
                            {w.vineyard_name && (
                              <p className="text-xs text-stone-500 mt-1">{w.vineyard_name}</p>
                            )}
                          </div>
                          <button
                            onClick={() => removeWine(w.id)}
                            className="text-stone-600 hover:text-red-400 transition-colors"
                            aria-label="Remove wine from comparison"
                            data-testid={`compare-remove-${w.id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <Row label="Score">
                    {wines.map((w) => (
                      <td key={w.id} className="px-4 py-4 align-top">
                        {w.average_rating > 0 ? (
                          <WineScore score={w.average_rating} size="sm" />
                        ) : (
                          <span className="text-stone-600">—</span>
                        )}
                      </td>
                    ))}
                  </Row>
                  <Row label="Ratings">
                    {wines.map((w) => (
                      <td key={w.id} className="px-4 py-4 align-top text-stone-300">
                        {w.rating_count || 0}
                      </td>
                    ))}
                  </Row>
                  <Row label="Vintage">
                    {wines.map((w) => (
                      <td key={w.id} className="px-4 py-4 align-top text-stone-300">
                        {w.vintage ? (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {w.vintage}
                          </span>
                        ) : '—'}
                      </td>
                    ))}
                  </Row>
                  <Row label="Alcohol">
                    {wines.map((w) => (
                      <td key={w.id} className="px-4 py-4 align-top text-stone-300">
                        {w.alcohol_content ? `${w.alcohol_content}%` : '—'}
                      </td>
                    ))}
                  </Row>
                  <Row label="Price Range">
                    {wines.map((w) => (
                      <td key={w.id} className="px-4 py-4 align-top text-stone-300">
                        {w.price_range || '—'}
                      </td>
                    ))}
                  </Row>
                  <Row label="Aroma">
                    {wines.map((w) => (
                      <td key={w.id} className="px-4 py-4 align-top text-stone-300">
                        {w.aroma || <span className="text-stone-600">—</span>}
                      </td>
                    ))}
                  </Row>
                  <Row label="Flavor">
                    {wines.map((w) => (
                      <td key={w.id} className="px-4 py-4 align-top text-stone-300">
                        {w.flavor || <span className="text-stone-600">—</span>}
                      </td>
                    ))}
                  </Row>
                  <Row label="Finish">
                    {wines.map((w) => (
                      <td key={w.id} className="px-4 py-4 align-top text-stone-300">
                        {w.finish || <span className="text-stone-600">—</span>}
                      </td>
                    ))}
                  </Row>
                  <Row label="Tasting Notes">
                    {wines.map((w) => (
                      <td key={w.id} className="px-4 py-4 align-top text-stone-400 text-xs leading-relaxed">
                        {w.tasting_notes || <span className="text-stone-600">—</span>}
                      </td>
                    ))}
                  </Row>
                  <Row label="Pairings">
                    {wines.map((w) => (
                      <td key={w.id} className="px-4 py-4 align-top">
                        {w.food_pairings?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {w.food_pairings.map((p, idx) => (
                              <span
                                key={`${w.id}-pair-${idx}`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-900 border border-stone-700 text-stone-400 text-[10px]"
                              >
                                <UtensilsCrossed className="w-2.5 h-2.5" />
                                {p}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-stone-600">—</span>
                        )}
                      </td>
                    ))}
                  </Row>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <tr className="border-b border-white/5 hover:bg-stone-900/20 transition-colors">
    <td className="px-4 py-4 align-top text-xs uppercase tracking-widest text-stone-500">
      {label}
    </td>
    {children}
  </tr>
);

export default WineCompare;
